/**
 * DOWNGRADE RESOURCE SELECTOR
 *
 * Modal-Dialog zur Auswahl der Ressourcen, die ein User
 * bei einem Plan-Downgrade behalten möchte.
 * Wird nach Bestätigung des Downgrades geöffnet.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { ArrowDown, Loader2, Check, BarChart3, List, Youtube, PenTool, AlertTriangle } from 'lucide-react';
import { TWModal, TWModalFooter, TWModalButton } from '../ui/TWModal';
import {
  getDowngradeResources,
  getResourceSelection,
  saveResourceSelection,
  type UserResourceSummary,
  type DowngradeResourceSelection,
} from '../../services/payment/subscriptionService';
import { twToast } from '../ui/TWToast';

// =====================================================
// TYPES
// =====================================================

interface DowngradeResourceSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  effectiveDate: string;
}

type ResourceTab = 'assets' | 'watchlists' | 'youtube' | 'charts';

interface TabConfig {
  key: ResourceTab;
  label: string;
  icon: React.ReactNode;
}

// =====================================================
// HELPER: Bild-URL erzeugen
// =====================================================

const getImageUrl = (path: string | null | undefined): string => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const isLocal = window.location.hostname === 'localhost';
  const baseUrl = isLocal ? 'http://localhost:5555' : window.location.origin;
  return baseUrl + path;
};

// =====================================================
// TABS KONFIGURATION
// =====================================================

const TABS: TabConfig[] = [
  { key: 'assets', label: 'Assets', icon: <BarChart3 className="w-4 h-4" /> },
  { key: 'watchlists', label: 'Watchlists', icon: <List className="w-4 h-4" /> },
  { key: 'youtube', label: 'YouTube', icon: <Youtube className="w-4 h-4" /> },
  { key: 'charts', label: 'Charts', icon: <PenTool className="w-4 h-4" /> },
];

// =====================================================
// KOMPONENTE
// =====================================================

export const DowngradeResourceSelector: React.FC<DowngradeResourceSelectorProps> = ({
  isOpen,
  onClose,
  onSaved,
  effectiveDate,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resources, setResources] = useState<UserResourceSummary | null>(null);
  const [activeTab, setActiveTab] = useState<ResourceTab>('assets');

  // Auswahl-State (Sets für schnelle Lookups)
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [selectedWatchlists, setSelectedWatchlists] = useState<Set<string>>(new Set());
  const [selectedChannels, setSelectedChannels] = useState<Set<string>>(new Set());
  const [selectedLayouts, setSelectedLayouts] = useState<Set<string>>(new Set());

  // =====================================================
  // DATEN LADEN
  // =====================================================

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [resourceData, existingSelection] = await Promise.all([
        getDowngradeResources(),
        getResourceSelection(),
      ]);

      setResources(resourceData);

      // Bestehende Auswahl laden oder Default (älteste bis Limit)
      if (existingSelection && existingSelection.status === 'pending') {
        setSelectedAssets(new Set(existingSelection.retainedAssetIds));
        setSelectedWatchlists(new Set(existingSelection.retainedWatchlistIds));
        setSelectedChannels(new Set(existingSelection.retainedYoutubeChannelIds));
        setSelectedLayouts(new Set(existingSelection.retainedChartLayoutIds));
      } else {
        // Default: Älteste Ressourcen vorauswählen (nach added_at sortiert)
        applyDefaultSelection(resourceData);
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Ressourcen');
    } finally {
      setLoading(false);
    }
  }, []);

  const applyDefaultSelection = (data: UserResourceSummary) => {
    const selectFirst = (items: Array<{ id: string }>, limit: number | null): Set<string> => {
      if (limit === null) return new Set(items.map(i => i.id));
      return new Set(items.slice(0, limit).map(i => i.id));
    };

    setSelectedAssets(selectFirst(data.assets, data.limits.assets));
    setSelectedWatchlists(selectFirst(data.watchlists, data.limits.watchlists));
    setSelectedChannels(selectFirst(data.youtubeChannels, data.limits.youtubeChannels));
    setSelectedLayouts(selectFirst(data.chartLayouts, data.limits.chartLayouts));
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, loadData]);

  // =====================================================
  // TOGGLE-HANDLER
  // =====================================================

  const createToggle = (
    selected: Set<string>,
    setSelected: React.Dispatch<React.SetStateAction<Set<string>>>,
    limit: number | null
  ) => (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        // Limit prüfen
        if (limit !== null && next.size >= limit) return prev;
        next.add(id);
      }
      return next;
    });
  };

  const toggleAsset = createToggle(selectedAssets, setSelectedAssets, resources?.limits.assets ?? null);
  const toggleWatchlist = createToggle(selectedWatchlists, setSelectedWatchlists, resources?.limits.watchlists ?? null);
  const toggleChannel = createToggle(selectedChannels, setSelectedChannels, resources?.limits.youtubeChannels ?? null);
  const toggleLayout = createToggle(selectedLayouts, setSelectedLayouts, resources?.limits.chartLayouts ?? null);

  // =====================================================
  // SPEICHERN
  // =====================================================

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveResourceSelection({
        retainedAssetIds: Array.from(selectedAssets),
        retainedWatchlistIds: Array.from(selectedWatchlists),
        retainedYoutubeChannelIds: Array.from(selectedChannels),
        retainedChartLayoutIds: Array.from(selectedLayouts),
      });

      twToast.success('Ressourcen-Auswahl gespeichert');
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // VALIDIERUNG
  // =====================================================

  const isValid = useMemo(() => {
    if (!resources) return false;
    const { limits } = resources;
    if (limits.assets !== null && selectedAssets.size > limits.assets) return false;
    if (limits.watchlists !== null && selectedWatchlists.size > limits.watchlists) return false;
    if (limits.youtubeChannels !== null && selectedChannels.size > limits.youtubeChannels) return false;
    if (limits.chartLayouts !== null && selectedLayouts.size > limits.chartLayouts) return false;
    return true;
  }, [resources, selectedAssets, selectedWatchlists, selectedChannels, selectedLayouts]);

  // Tabs mit Relevanz-Info (nur Tabs anzeigen die Ressourcen haben)
  const relevantTabs = useMemo(() => {
    if (!resources) return TABS;
    return TABS.filter(tab => {
      switch (tab.key) {
        case 'assets': return resources.assets.length > 0;
        case 'watchlists': return resources.watchlists.length > 0;
        case 'youtube': return resources.youtubeChannels.length > 0;
        case 'charts': return resources.chartLayouts.length > 0;
        default: return true;
      }
    });
  }, [resources]);

  // Sicherstellen dass aktiver Tab relevant ist
  useEffect(() => {
    const firstTab = relevantTabs[0];
    if (firstTab && !relevantTabs.find(t => t.key === activeTab)) {
      setActiveTab(firstTab.key);
    }
  }, [relevantTabs, activeTab]);

  // =====================================================
  // COUNTER-HILFSFUNKTION
  // =====================================================

  const getCounter = (tab: ResourceTab): { selected: number; total: number; limit: number | null } => {
    if (!resources) return { selected: 0, total: 0, limit: null };
    switch (tab) {
      case 'assets': return { selected: selectedAssets.size, total: resources.assets.length, limit: resources.limits.assets };
      case 'watchlists': return { selected: selectedWatchlists.size, total: resources.watchlists.length, limit: resources.limits.watchlists };
      case 'youtube': return { selected: selectedChannels.size, total: resources.youtubeChannels.length, limit: resources.limits.youtubeChannels };
      case 'charts': return { selected: selectedLayouts.size, total: resources.chartLayouts.length, limit: resources.limits.chartLayouts };
    }
  };

  const isOverLimit = (tab: ResourceTab): boolean => {
    if (!resources) return false;
    const { selected, total, limit } = getCounter(tab);
    return limit !== null && total > limit;
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <TWModal
      isOpen={isOpen}
      onClose={onClose}
      title="Ressourcen auswählen"
      icon={<ArrowDown className="w-5 h-5 text-orange-500" />}
      size="xl"
    >
      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand" />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Content */}
      {resources && !loading && (
        <div className="space-y-4">
          {/* Info-Banner */}
          <div className="rounded-lg border border-orange-200 dark:border-orange-800/50 bg-orange-50 dark:bg-orange-900/10 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
              <div className="text-xs text-orange-700 dark:text-orange-400">
                <p className="font-medium mb-1">
                  Wähle die Ressourcen aus, die du behalten möchtest.
                </p>
                <p>
                  Nicht ausgewählte Ressourcen werden am{' '}
                  <strong>{new Date(effectiveDate).toLocaleDateString('de-DE')}</strong>{' '}
                  unwiderruflich gelöscht. Du kannst die Auswahl bis dahin jederzeit ändern.
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
            {relevantTabs.map(tab => {
              const counter = getCounter(tab.key);
              const overLimit = isOverLimit(tab.key);
              const isActive = activeTab === tab.key;

              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
                    flex items-center gap-2 px-4 py-2.5 text-sm font-medium
                    border-b-2 -mb-px transition-colors
                    ${isActive
                      ? 'border-brand text-brand'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }
                  `}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                  {counter.limit !== null && (
                    <span className={`
                      text-xs px-1.5 py-0.5 rounded-full font-medium
                      ${overLimit
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }
                    `}>
                      {counter.selected}/{counter.limit}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="max-h-[50vh] overflow-y-auto pr-1">
            {activeTab === 'assets' && (
              <ResourceGrid
                items={resources.assets}
                selected={selectedAssets}
                onToggle={toggleAsset}
                limit={resources.limits.assets}
                renderItem={(item) => (
                  <>
                    {item.logoPath ? (
                      <img
                        src={getImageUrl(item.logoPath)}
                        alt={item.symbol}
                        className="w-8 h-8 rounded-full object-cover bg-gray-100 dark:bg-gray-800"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-500">
                        {item.symbol.slice(0, 2)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {item.symbol}
                        {item.watchlistCount > 0 && (
                          <span className="ml-1.5 text-gray-400">
                            · in {item.watchlistCount} {item.watchlistCount === 1 ? 'Watchlist' : 'Watchlists'}
                          </span>
                        )}
                      </p>
                    </div>
                  </>
                )}
              />
            )}

            {activeTab === 'watchlists' && (
              <ResourceGrid
                items={resources.watchlists}
                selected={selectedWatchlists}
                onToggle={toggleWatchlist}
                limit={resources.limits.watchlists}
                renderItem={(item) => (
                  <>
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: item.color || '#6B7280' }}
                    >
                      <List className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {item.name}
                        {item.isDefault && (
                          <span className="ml-1.5 text-xs text-gray-400">(Standard)</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {item.itemCount} {item.itemCount === 1 ? 'Asset' : 'Assets'}
                      </p>
                    </div>
                  </>
                )}
              />
            )}

            {activeTab === 'youtube' && (
              <ResourceGrid
                items={resources.youtubeChannels}
                selected={selectedChannels}
                onToggle={toggleChannel}
                limit={resources.limits.youtubeChannels}
                renderItem={(item) => (
                  <>
                    {item.thumbnailPath ? (
                      <img
                        src={getImageUrl(item.thumbnailPath)}
                        alt={item.channelTitle}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <Youtube className="w-4 h-4 text-red-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {item.channelTitle}
                      </p>
                    </div>
                  </>
                )}
              />
            )}

            {activeTab === 'charts' && (
              <ResourceGrid
                items={resources.chartLayouts}
                selected={selectedLayouts}
                onToggle={toggleLayout}
                limit={resources.limits.chartLayouts}
                renderItem={(item) => (
                  <>
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                      <PenTool className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {item.drawingCount} {item.drawingCount === 1 ? 'Zeichnung' : 'Zeichnungen'}
                      </p>
                    </div>
                  </>
                )}
              />
            )}
          </div>

          {/* Footer */}
          <TWModalFooter>
            <TWModalButton variant="cancel" onClick={onClose}>
              Später
            </TWModalButton>
            <TWModalButton
              variant="primary"
              onClick={handleSave}
              loading={saving}
              disabled={!isValid}
            >
              Auswahl speichern
            </TWModalButton>
          </TWModalFooter>
        </div>
      )}
    </TWModal>
  );
};

// =====================================================
// RESOURCE GRID - Wiederverwendbare Auswahl-Liste
// =====================================================

interface ResourceGridProps<T extends { id: string }> {
  items: T[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  limit: number | null;
  renderItem: (item: T) => React.ReactNode;
}

function ResourceGrid<T extends { id: string }>({
  items,
  selected,
  onToggle,
  limit,
  renderItem,
}: ResourceGridProps<T>) {
  if (items.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
        Keine Ressourcen vorhanden
      </p>
    );
  }

  // Kein Limit oder alle passen rein -> keine Auswahl nötig
  if (limit === null || items.length <= limit) {
    return (
      <div className="rounded-lg border border-green-200 dark:border-green-800/50 bg-green-50 dark:bg-green-900/10 p-3">
        <p className="text-xs text-green-700 dark:text-green-400 text-center">
          Alle {items.length} Einträge passen in den neuen Plan. Keine Auswahl erforderlich.
        </p>
      </div>
    );
  }

  const atLimit = limit !== null && selected.size >= limit;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {items.map(item => {
        const isSelected = selected.has(item.id);
        const isDisabled = !isSelected && atLimit;

        return (
          <button
            key={item.id}
            onClick={() => !isDisabled && onToggle(item.id)}
            disabled={isDisabled}
            className={`
              flex items-center gap-3 p-3 rounded-lg border text-left
              transition-all duration-150
              ${isSelected
                ? 'border-brand bg-brand/5 dark:bg-brand/10 ring-1 ring-brand/30'
                : isDisabled
                  ? 'border-gray-200 dark:border-gray-700 opacity-40 cursor-not-allowed'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer'
              }
            `}
          >
            {/* Checkbox */}
            <div className={`
              w-5 h-5 rounded border-2 shrink-0
              flex items-center justify-center transition-colors
              ${isSelected
                ? 'bg-brand border-brand'
                : 'border-gray-300 dark:border-gray-600'
              }
            `}>
              {isSelected && <Check className="w-3 h-3 text-gray-900" />}
            </div>

            {/* Content (vom Parent gerendert) */}
            {renderItem(item)}
          </button>
        );
      })}
    </div>
  );
}

export default DowngradeResourceSelector;
